import Vue from "vue";
import Vuex from "vuex";
import { loadFilterSettings, saveFilterSettings } from "@/features/transportsFilterSettings/transportsFilterSettings";
import { updaterPlugin } from "@/store/updaterPlugin";
import { buildAuthModule } from "@/features/auth/store";
import { buildCarriersModule } from "@/features/carriers/store";
import { buildErrorMessagesModule } from "@/features/errorMessages/store";
import { buildFeaturesModule } from "@/features/featureToggle/store";
import { buildLocationArrivalsModule } from "@/features/locationArrivals/store";
import { buildNotificationsModule } from "@/features/notifications/store";
import { buildSearchTransportsModule } from "@/features/search/store";
import { buildShippersModule } from "@/features/shippers/store";
import { buildTransportDetailsModule } from "@/features/transportDetails/store";
import { buildTransportColumnsSettingsModule } from "@/features/transportTableColumnsSettings/store";
import {
  buildTransportsModule,
  buildCancelledTransportsModule,
  buildCompletedTransportsModule,
  buildActionsRequiredModule,
} from "@/features/transports/store";
import { buildVersionCheckModule } from "@/features/versionCheck/store";
import { PAGES } from "@/router/pageNames";
import {
  getActionRequiredTransports,
  getAreaArrivedTransports,
  getCancelledTransports,
  getCompletedTransports,
  getDepartedTransports,
  getGpsLostTransports,
  getGpsLowTransports,
  getGpsMissingTransports,
  getIncomingTransports,
  getNoDepartureTransports,
  getOngoingArrivedTransports,
  getPresumablyIncomingTransports,
  getManuallyCompletedTransports,
  getOngoingTransports,
  getPresumablyOngoingTransports,
  getOverdueTransports,
  getPlannedTransports,
  getTrackingIdMissingTransports,
  getWaypointConfirmations,
} from "@/features/transports/api";
import { getFeatures } from "@/features/featureToggle/api";
import { buildWatchlistModule } from "@/features/watchlist/store";

Vue.use(Vuex);

const EVENT = {
  CURRENT_PAGE_CHANGED: "CURRENT_PAGE_CHANGED",
  FILTERS_CHANGED: "FILTERS_CHANGED",
  LAST_VIEWED_TRANSPORT_CHANGED: "LAST_VIEWED_TRANSPORT_CHANGED",
  NAV_MENU_TOGGLED: "NAV_MENU_TOGGLED",
  TABLE_SCROLL_POSITION_CHANGED: "TABLE_SCROLL_POSITION_CHANGED",
};

const defaultState = () => ({
  currentPage: "",
  ui: {
    lastViewedTransportId: "",
    showNavMenu: false,
    transportTableFilters: loadFilterSettings(),
    transportTableScrollPosition: { page: "", position: null },
  },
});

const mutations = {
  [EVENT.CURRENT_PAGE_CHANGED](state, page) {
    state.currentPage = page;
  },

  [EVENT.FILTERS_CHANGED](state, filters) {
    state.ui.transportTableFilters = filters;
  },

  [EVENT.LAST_VIEWED_TRANSPORT_CHANGED](state, transportId) {
    state.ui.lastViewedTransportId = transportId;
  },

  [EVENT.NAV_MENU_TOGGLED](state) {
    state.ui.showNavMenu = !state.ui.showNavMenu;
  },

  [EVENT.TABLE_SCROLL_POSITION_CHANGED](state, { page, position }) {
    state.ui.transportTableScrollPosition = {
      page,
      position,
    };
  },
};

const actions = {
  getAllTransports({ state }) {
    this.dispatch("watchlist/fetch");
    this.dispatch("actionRequiredTransports/fetch");
    this.dispatch("areaArrivedTransports/fetch");
    this.dispatch("completedTransports/fetch");
    this.dispatch("departedTransports/fetch");
    this.dispatch("gpsLostTransports/fetch");
    this.dispatch("gpsLowTransports/fetch");
    this.dispatch("gpsMissingTransports/fetch");
    this.dispatch("noDepartureFromDestinationTransports/fetch");
    this.dispatch("ongoingArrivedTransports/fetch");
    this.dispatch("waypointConfirmations/fetch");
    this.dispatch("incomingTransports/fetch");
    this.dispatch("presumablyIncomingTransports/fetch");
    this.dispatch("manuallyCompletedTransports/fetch");
    this.dispatch("ongoingTransports/fetch");
    this.dispatch("presumablyOngoingTransports/fetch");
    this.dispatch("overdueTransports/fetch");
    this.dispatch("plannedTransports/fetch");
    this.dispatch("trackingIdMissingTransports/fetch");
    this.dispatch("cancelledTransports/fetch");

    this.dispatch("carriers/fetch");
    this.dispatch("shippers/fetch");
    this.dispatch("shippers/fetchShippersWithTransports");
    this.dispatch("carriers/fetchCarriersWithTransports");

    if (state.searchResults.searchText) {
      this.dispatch("searchResults/fetch");
    }
  },

  setCurrentPage({ commit }, page) {
    commit(EVENT.CURRENT_PAGE_CHANGED, page);
  },

  setFilters({ commit, state }, filters) {
    let newFilters = {};

    if (filters !== null) {
      newFilters = {
        ...state.ui.transportTableFilters,
        ...filters,
      };
    }

    saveFilterSettings(newFilters);
    commit(EVENT.FILTERS_CHANGED, newFilters);
  },

  setLastViewedTransport({ commit }, transportId) {
    commit(EVENT.LAST_VIEWED_TRANSPORT_CHANGED, transportId);
  },

  setTableScrollPosition({ commit }, { page, position }) {
    commit(EVENT.TABLE_SCROLL_POSITION_CHANGED, { page, position });
  },

  toggleNavMenu({ commit }) {
    commit(EVENT.NAV_MENU_TOGGLED);
  },
};

const getters = {
  async isAdmin() {
    const enabled = await getFeatures();
    return enabled.includes("adminUi");
  },

  profile() {
    const idToken = store.getters["auth/idToken"];

    if (!idToken) {
      return {};
    }

    return {
      email: idToken.email,
      familyName: idToken.family_name,
      givenName: idToken.given_name,
      nickname: idToken.nickname,
      picture: idToken.picture,
    };
  },

  transportTableFilters(state) {
    return state.ui.transportTableFilters;
  },
};

const modules = {
  watchlist: buildWatchlistModule(),
  actionRequiredTransports: buildActionsRequiredModule({
    requestTransports: getActionRequiredTransports,
  }),
  areaArrivedTransports: buildCompletedTransportsModule({
    requestTransports: getAreaArrivedTransports,
  }),
  auth: buildAuthModule(),
  cancelledTransports: buildCancelledTransportsModule({
    requestTransports: getCancelledTransports,
  }),
  carriers: buildCarriersModule(),
  completedTransports: buildCompletedTransportsModule({
    requestTransports: getCompletedTransports,
  }),
  departedTransports: buildCompletedTransportsModule({
    requestTransports: getDepartedTransports,
  }),
  errorMessages: buildErrorMessagesModule(),
  features: buildFeaturesModule(),
  gpsLostTransports: buildActionsRequiredModule({
    requestTransports: getGpsLostTransports,
  }),
  gpsLowTransports: buildActionsRequiredModule({
    requestTransports: getGpsLowTransports,
  }),
  gpsMissingTransports: buildActionsRequiredModule({
    requestTransports: getGpsMissingTransports,
  }),
  incomingTransports: buildTransportsModule({
    requestTransports: getIncomingTransports,
  }),
  presumablyIncomingTransports: buildTransportsModule({
    requestTransports: getPresumablyIncomingTransports,
  }),
  locationArrivals: buildLocationArrivalsModule(),
  manuallyCompletedTransports: buildCompletedTransportsModule({
    requestTransports: getManuallyCompletedTransports,
  }),
  noDepartureFromDestinationTransports: buildCompletedTransportsModule({
    requestTransports: getNoDepartureTransports,
  }),
  notifications: buildNotificationsModule(),
  ongoingArrivedTransports: buildCompletedTransportsModule({
    requestTransports: getOngoingArrivedTransports,
  }),
  ongoingTransports: buildTransportsModule({
    requestTransports: getOngoingTransports,
  }),
  presumablyOngoingTransports: buildTransportsModule({
    requestTransports: getPresumablyOngoingTransports,
  }),
  overdueTransports: buildCompletedTransportsModule({
    requestTransports: getOverdueTransports,
  }),
  plannedTransports: buildTransportsModule({
    requestTransports: getPlannedTransports,
  }),
  searchResults: buildSearchTransportsModule(),
  shippers: buildShippersModule(),
  trackingIdMissingTransports: buildActionsRequiredModule({
    requestTransports: getTrackingIdMissingTransports,
  }),
  transportDetails: buildTransportDetailsModule(),
  transportTableColumnsSettings: buildTransportColumnsSettingsModule(),
  versionCheck: buildVersionCheckModule(),
  waypointConfirmations: buildActionsRequiredModule({
    requestTransports: getWaypointConfirmations,
  }),
};

const ONE_MINUTE_IN_MS = 1000 * 60;
const updateAllowed = () => true;

const plugins = [
  updaterPlugin({
    action: "notifications/getNotifications",
    pages: [`!${PAGES.TRANSPORT_TRACKING}`, `!${PAGES.NO_ORGANIZATION}`],
    pollingIntervalMs: ONE_MINUTE_IN_MS,
    updateAllowed,
  }),
  updaterPlugin({
    action: "getAllTransports",
    pages: [`!${PAGES.TRANSPORT_TRACKING}`, `!${PAGES.NO_ORGANIZATION}`],
    pollingIntervalMs: ONE_MINUTE_IN_MS,
    updateAllowed,
  }),
  updaterPlugin({
    action: "transportDetails/refresh",
    pages: [PAGES.TRANSPORT_DETAILS],
    pollingIntervalMs: ONE_MINUTE_IN_MS,
    updateAllowed,
  }),
  updaterPlugin({
    action: "versionCheck/getVersion",
    pages: [`!${PAGES.TRANSPORT_TRACKING}`],
    pollingIntervalMs: ONE_MINUTE_IN_MS * 15,
  }),
];

const store = new Vuex.Store({
  state: defaultState(),
  mutations,
  actions,
  getters,
  modules,
  plugins,
});

export default store;
